import { Button } from '@acpaas-ui/react-components';
import _ from 'lodash';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchReset } from '../../../actions/fetch.actions';
import { resourceUpdate } from '../../../actions/resources.actions';
import { MODALS } from '../../../constants/modals.constants';
import { TOAST_TYPES } from '../../../constants/settings.constants';
import { useForm } from '../../../hooks/useForm.hook';
import { useModal } from '../../../hooks/useModal.hook';
import { useResourceInput } from '../../../hooks/useResourceInput';
import { getAttachmentsForMeasureMoment, goalIndicatorToTitle } from '../../../lib/dossier.utils';
import DeleteWarningModal from '../../../modals/DeleteWarningModal';
import MeasureMomentCreateModal from '../../../modals/MeasureMomentCreateModal';
import MeasureMomentDetailsModal from '../../../modals/MeasureMomentDetailsModal';
import { MEASURE_MOMENTS_RESOURCE } from '../../../resources/measure.moments.resource';
import procedureService from '../../../services/api/procedure.service';
import toastService from '../../../services/toasts.service';
import DossierSectionWrapper from '../../dossier-details/DossierSectionWrapper';
import TextArea from '../../forms/TextArea';
import TextField from '../../forms/TextField';
import DossierProcedureIndicator from './DossierProcedureIndicator';

const DossierProcedureGoal = ({
  dossierId,
  id,
  name,
  evaluation,
  indicators,
  openedIndicators,
  setOpenedIndicators,
  attachments,
  onDelete,
  onUpdated,
  onDossierRefetch,
  onAttachmentsRefetch,
  write
}) => {
  const dispatch = useDispatch();
  const { isSubmitting, data, reset, input, submitStart, submitOk, submitError } = useForm({
    id,
    name,
    evaluation,
    indicators
  });
  const resource = {
    isFetching: false,
    isSubmitting,
    getValue: (field) => data?.[field],
    setValue: (value, field, options = {}) => {
      submitStart();
      procedureService.updateGoal(
        dossierId,
        data?.id,
        { [field]: value },
        {
          onSuccess: () => {
            submitOk();
            input(value, field);
            onUpdated(value, field, data?.id);
            options.onSuccess();
          },
          onError: (message) => {
            submitError({ [field]: message });
            options.onError(message);
          }
        }
      );
    },
    onCancel: reset
  };
  const deleteModal = useModal(MODALS.GOAL_INDICATOR_CONFIRM_DELETE);
  const deleteMeasureMomentModal = useModal(MODALS.MEASURE_MOMENT_CONFIRM_DELETE);
  const measureMomentCreateModal = useModal(MODALS.MEASURE_MOMENT_CREATE);
  const measureMomentDetailModal = useModal(MODALS.MEASURE_MOMENT_DETAILS);
  const [creatingIndicator, setCreatingIndicator] = useState(false);
  const [deletingIndicatorId, setDeletingIndicatorId] = useState('');
  const [addingIndicatorId, setAddingIndicatorId] = useState('');
  const measureMomentDetailForm = useForm(MODALS.MEASURE_MOMENT_DETAILS.initialState);

  const handleAdd = () => {
    setCreatingIndicator(true);
    procedureService.addGoalIndicator(dossierId, id, {
      onSuccess: (resp) => {
        setCreatingIndicator(false);
        onDossierRefetch();
        setOpenedIndicators([resp.id]);
      },
      onError: (message) => {
        toastService.showToast(TOAST_TYPES.ERROR, message);
        setCreatingIndicator(false);
      }
    });
  };

  const handleUpdated = (value, field, indicatorId) => {
    const updatedIndicatorIndex = indicators.findIndex((i) => i.id === indicatorId);
    if (updatedIndicatorIndex === -1) {
      return;
    }

    const newIndicators = _.cloneDeep(indicators);
    newIndicators[updatedIndicatorIndex] = { ...newIndicators[updatedIndicatorIndex], [field]: value };
    onUpdated(newIndicators, 'indicators', id);
  };

  const handleMeasureMomentUpdated = (value, field, indicatorId, measureMomentId) => {
    const updatedIndicatorIndex = indicators.findIndex((i) => i.id === indicatorId);
    if (updatedIndicatorIndex === -1) {
      return;
    }
    const updatedMeasureMomentIndex = indicators[updatedIndicatorIndex].measureMoments?.findIndex(
      (mm) => mm.id === measureMomentId
    );
    if (updatedMeasureMomentIndex === -1) {
      return;
    }

    const newMeasureMoments = _.cloneDeep(indicators[updatedIndicatorIndex]?.measureMoments ?? []);
    newMeasureMoments[updatedMeasureMomentIndex] = { ...newMeasureMoments[updatedMeasureMomentIndex], [field]: value };
    handleUpdated(newMeasureMoments, 'measureMoments', indicatorId);
  };

  const handleDeleteConfirm = (indicatorId) => {
    setDeletingIndicatorId(indicatorId);
    deleteModal.onOpen();
  };

  const handleDelete = (onError, onSuccess) => {
    procedureService.deleteGoalIndicator(dossierId, id, deletingIndicatorId, {
      onSuccess: (resp) => {
        onSuccess(resp);
        onDossierRefetch();
      },
      onError
    });
  };

  const handleDeleteMeasureMomentConfirm = () => {
    deleteMeasureMomentModal.onOpen();
  };

  const handleDeleteMeasureMoment = (onError, onSuccess) => {
    const indicatorId = measureMomentDetailForm.data.indicatorId;
    procedureService.deleteIndicatorMeasureMoment(dossierId, id, indicatorId, measureMomentDetailForm.data.id, {
      onSuccess: (resp) => {
        onSuccess(resp);
        onDossierRefetch();
        dispatch(
          resourceUpdate(
            { key: `${MEASURE_MOMENTS_RESOURCE.key}_${indicatorId}` },
            'data',
            resp?.dossier?.procedure?.goals?.find((g) => g.id === id)?.indicators?.find((i) => i.id === indicatorId)
              ?.measureMoments ?? []
          )
        );
        dispatch(fetchReset({ key: `${MEASURE_MOMENTS_RESOURCE.key}_${indicatorId}` }));
      },
      onError
    });
  };

  const handleMeasureMomentAddOpen = (indicatorId) => {
    setAddingIndicatorId(indicatorId);
    measureMomentCreateModal.onOpen();
  };

  const handleMeasureMomentOpen = (measureMoment, indicatorId) => {
    measureMomentDetailForm.load({
      ...measureMoment,
      indicatorId,
      attachments: getAttachmentsForMeasureMoment(measureMoment, attachments)
    });
    measureMomentDetailModal.onOpen();
  };

  return (
    <div className="spo-m-dossier-procedure-goal">
      <div className="spo-m-dossier-procedure-goal__name u-margin-bottom">
        <div className="spo-m-dossier-procedure-goal__name-text-wrapper">
          <TextField label="Naam langetermijndoel" {...useResourceInput('name', resource)} readOnly={!write} />
        </div>
        {write && <Button icon="ai-bin" type="danger" transparent onClick={onDelete} />}
      </div>
      <DossierSectionWrapper
        className="u-margin-bottom"
        headerClass="dossier-section-indicator"
        title="Indicatoren"
        addLabel="Indicator toevoegen"
        noItemsLabel="Er zijn momenteel nog geen indicatoren toegevoegd."
        onItemHeaderClick={(open, id) => {
          const newValue = open ? [...(openedIndicators ?? []), id] : openedIndicators?.filter((oc) => oc !== id) ?? [];
          setOpenedIndicators(newValue);
        }}
        openedItemIds={openedIndicators}
        onAddItem={write ? handleAdd : undefined}
        isAdding={creatingIndicator}
        items={indicators?.map((indicator) => ({
          id: indicator.id,
          title: goalIndicatorToTitle(indicator),
          Component: (
            <DossierProcedureIndicator
              {...indicator}
              write={write}
              goalId={id}
              dossierId={dossierId}
              onMeasureMomentOpen={(measureMoment) => handleMeasureMomentOpen(measureMoment, indicator.id)}
              onMeasureMomentAdd={handleMeasureMomentAddOpen}
              onDelete={() => handleDeleteConfirm(indicator.id)}
              onUpdated={handleUpdated}
            />
          )
        }))}
      />
      <div className="row">
        <div className="col-xs-12">
          <TextArea
            label="Evaluatie langetermijndoel"
            size="small"
            {...useResourceInput('evaluation', resource)}
            readOnly={!write}
          />
        </div>
      </div>
      <MeasureMomentCreateModal
        {...measureMomentCreateModal}
        goalId={id}
        dossierId={dossierId}
        addingIndicatorId={addingIndicatorId}
        onDossierRefetch={onDossierRefetch}
        onAttachmentsRefetch={onAttachmentsRefetch}
      />
      {measureMomentDetailModal.isOpen && (
        <MeasureMomentDetailsModal
          {...measureMomentDetailModal}
          write={write}
          dossierId={dossierId}
          goalId={id}
          form={measureMomentDetailForm}
          onDelete={handleDeleteMeasureMomentConfirm}
          onUpdated={handleMeasureMomentUpdated}
          onAttachmentsRefetch={onAttachmentsRefetch}
        />
      )}
      <DeleteWarningModal
        {...deleteModal}
        warningText="Ben je zeker dat je deze indicator wilt verwijderen? Alle informatie die hieraan gekoppeld is zal verloren gaan."
        onConfirm={handleDelete}
      />
      {deleteMeasureMomentModal.isOpen && (
        <DeleteWarningModal
          {...deleteMeasureMomentModal}
          warningText="Ben je zeker dat je dit meetmoment wilt verwijderen? Alle informatie die hieraan gekoppeld is zal verloren gaan."
          onConfirm={handleDeleteMeasureMoment}
        />
      )}
    </div>
  );
};

export default DossierProcedureGoal;
